import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import GreenButton from '../Components/Buttons/Greenbutton';
import { getAccount, updateAccount } from '../lib/api';
import { getSavedAddressLine } from '../lib/addressDisplay';

function ProfilePage() {
  const { user, isAuthenticated, updateUser } = useAuth();
  const [form, setForm] = useState({ fullName: '', phone: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let isMounted = true;

    async function loadAccount() {
      try {
        const response = await getAccount({ userId: user.id });
        if (!isMounted) {
          return;
        }

        setForm({
          fullName: response.user.fullName || '',
          phone: response.user.phone || '',
        });
      } catch (loadError) {
        if (isMounted) {
          setError(loadError.message);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    if (user?.id) {
      loadAccount();
    }

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setIsSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await updateAccount({
        userId: user.id,
        updates: form,
      });
      updateUser(response.user);
      setNotice('Profile updated.');
    } catch (saveError) {
      setError(saveError.message);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section className="page-card profile-page account-page">
      <div className="profile-page__hero account-page__header">
        <div className="profile-page__avatar">
          {user.fullName.slice(0, 1).toUpperCase()}
        </div>
        <div>
          <p className="profile-page__eyebrow">Account Profile</p>
          <h1 className="page-card__title">{user.fullName}</h1>
          <p className="page-card__text">
            Manage your customer details, saved address, order history, and account settings.
          </p>
        </div>
        <div className="account-page__tabs">
          <Link to="/profile" className="is-active">Profile</Link>
          <Link to="/orders">Orders</Link>
          <Link to="/account/addresses">Addresses</Link>
          <Link to="/account/settings">Settings</Link>
        </div>
      </div>
      <div className="profile-page__grid">
        <article className="profile-page__card">
          <h2>Email</h2>
          <p>{user.email}</p>
        </article>
        <article className="profile-page__card">
          <h2>Account Created</h2>
          <p>{new Date(user.createdAt).toLocaleDateString()}</p>
        </article>
        <article className="profile-page__card">
          <h2>Status</h2>
          <p>Active customer account</p>
        </article>
        <article className="profile-page__card">
          <h2>Saved Address</h2>
          <p>{getSavedAddressLine(user.addressBook?.[0]) || 'No saved address yet'}</p>
        </article>
        <article className="profile-page__card">
          <h2>Settings</h2>
          <p>{user.preferences?.orderUpdates ? 'Order updates enabled' : 'Notifications limited'}</p>
        </article>
        <article className="profile-page__card">
          <h2>Order History</h2>
          <p className="auth-form__switch"><Link to="/orders">Open orders</Link></p>
        </article>
      </div>
      {isLoading ? <p className="products-state">Loading profile...</p> : null}
      {error ? <p className="products-state products-state--error">{error}</p> : null}
      {notice ? <p className="orders-success">{notice}</p> : null}
      <form className="auth-form account-form" onSubmit={handleSubmit}>
        <label className="auth-form__field">
          <span>Full Name</span>
          <input
            value={form.fullName}
            onChange={(event) => {
              setForm((current) => ({ ...current, fullName: event.target.value }));
            }}
          />
        </label>
        <label className="auth-form__field">
          <span>Phone</span>
          <input
            value={form.phone}
            onChange={(event) => {
              setForm((current) => ({ ...current, phone: event.target.value }));
            }}
          />
        </label>
        <GreenButton disabled={isSaving} type="submit" text={isSaving ? 'Saving...' : 'Save Profile'} />
      </form>
    </section>
  );
}

export default ProfilePage;
